import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import type { AdminScreenSessionPhase } from '@/hooks/useAdminScreenSession';

export interface AdminSessionTimeoutDialogProps {
  phase: AdminScreenSessionPhase;
  countdown: number;
  onExtend: () => void;
  onLogout: () => void;
}

export function AdminSessionTimeoutDialog({
  phase,
  countdown,
  onExtend,
  onLogout,
}: Readonly<AdminSessionTimeoutDialogProps>) {
  const { t } = useTranslation();
  const open = phase !== 'active';
  const isIdle = phase === 'idle-warning';
  const isUrgent = open && countdown <= 15;
  const progress = Math.max(0, Math.min(100, (countdown / 60) * 100));

  return (
    <Dialog
      open={open}
      onClose={() => undefined}
      aria-labelledby="admin-session-timeout-title"
      aria-describedby="admin-session-timeout-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogContent sx={{ pt: 3 }}>
        <Stack sx={{ alignItems: 'center', textAlign: 'center', gap: 2 }}>
          <WarningAmberIcon
            color={isUrgent ? 'error' : 'warning'}
            aria-hidden="true"
            sx={{ fontSize: 36 }}
          />
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={64}
              color={isUrgent ? 'error' : 'primary'}
              aria-hidden="true"
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {countdown}
              </Typography>
            </Box>
          </Box>
          <Typography
            id="admin-session-timeout-title"
            variant="h6"
            sx={{ fontWeight: 500 }}
          >
            {t('sessionTimeout.title')}
          </Typography>
          <Typography
            id="admin-session-timeout-description"
            variant="body2"
            color="text.secondary"
          >
            {t(isIdle ? 'sessionTimeout.idleMessage' : 'sessionTimeout.absoluteMessage', {
              seconds: countdown,
            })}
          </Typography>
          <Box
            component="span"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          >
            {t('sessionTimeout.countdownAnnouncement', { seconds: countdown })}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onLogout}>
          {t('sessionTimeout.logOut')}
        </Button>
        {isIdle && (
          <Button variant="contained" onClick={onExtend} autoFocus>
            {t('sessionTimeout.continue')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
